import type { InputHTMLAttributes } from 'react';
import { useId } from 'react';
import { variantColors, type ButtonVariant } from '../Button/variantColors';
import { BUTTON_SHADOW_PATH, BUTTON_BORDER_PATH, BUTTON_MAIN_PATH } from '../svg-paths';
import { createResponsiveTextStroke } from '@/styles/textStroke';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  variant?: ButtonVariant;
  className?: string;
}

export function TextInput({
  variant = 'grey',
  className = '',
  disabled,
  ...props
}: TextInputProps) {
  const gradientId = useId();
  const colors = variantColors[variant];

  return (
    <div
      className={`relative ${disabled ? 'opacity-50' : ''} ${className}`}
      style={{ containerType: 'size' }}
    >
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 403 106"
        aria-hidden="true"
      >
        {/* Shadow/base layer */}
        <path d={BUTTON_SHADOW_PATH} fill={colors.shadow} />
        {/* Black border */}
        <path d={BUTTON_BORDER_PATH} fill="#000" />
        {/* Main gradient input */}
        <path d={BUTTON_MAIN_PATH} fill={`url(#${gradientId})`} />
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="201"
            x2="201"
            y1="89"
            y2="4"
          >
            <stop stopColor={colors.gradient.start} />
            <stop offset="0.49" stopColor={colors.gradient.middle} />
            <stop offset="1" stopColor={colors.gradient.end} />
          </linearGradient>
        </defs>
      </svg>

      <input
        className="absolute inset-0 w-full h-full bg-transparent font-mono text-[#F2F2F2] leading-none text-center uppercase placeholder:text-[#F2F2F2]/50 focus:outline-none focus:ring-2 focus:ring-white/30 rounded-xl"
        style={{
          fontSize: 'clamp(1rem, 20cqw, 2.5rem)',
          fontWeight: 500,
          ...createResponsiveTextStroke(1, 0.06),
          padding: '0 1rem'
        }}
        disabled={disabled}
        {...props}
      />
    </div>
  );
}

export default TextInput;
